import { TemplateElement, html, defineElement, when, unsafeHTML } from '@webtides/element-js';
import style from './hero-video.css';
import '../../../button/button.js';
import '../../../chip/chip.js';

/**
 * @property {string} headline
 * @property {string} videoLandscapeUrl
 * @property {string} videoPortraitUrl
 * @property {boolean} showTitle
 * @property {('full-width'|'contained')} variant
 * @property {KickerProperties} kicker
 * @property {string} text
 * @property {LinkProperties[]} buttons
 */
export default class HeroVideo extends TemplateElement {
    constructor() {
        super({ styles: [style], shadowRender: true });
    }

    properties() {
        return {
            headline: '',
            videoLandscapeUrl: '',
            videoPortraitUrl: '',
            showTitle: false,
            variant: 'full-width',
            kicker: null,
            text: '',
            buttons: [],
        };
    }

    connected() {}

    videoTemplate(classes = '') {
        return html`
            <video class="${classes}" muted autoplay loop playsinline>
                <source src="${this.videoPortraitUrl}" media="(orientation: portrait)" />
                <source src="${this.videoLandscapeUrl}" media="(orientation: landscape)" />
            </video>
        `;
    }

    containedTemplate() {
        return html`
            <div class="container bg-white pt-16 md:relative md:pt-40">
                <div class="grid md:block">
                    ${this.videoTemplate(
                        'col-start-1 row-start-1 h-[80svh] w-full rounded-[16px] object-cover md:h-[500px] md:rounded-[24px]',
                    )}
                    <div
                        class="col-start-1 row-start-1 relative z-10 flex flex-col justify-between gap-16 p-16 text-white md:absolute md:left-[95px] md:top-[106px] md:max-w-[540px] md:gap-32 md:p-0"
                        data-surface="dark"
                    >
                        <div class="flex flex-col gap-8">
                            ${when(
                                this.kicker?.text,
                                this.kicker?.variant
                                    ? html`<mb-chip
                                          variant="${this.kicker.variant}"
                                          text="${this.kicker.text}"
                                          class="self-start mb-chip-size-sm md:mb-chip-size-md"
                                      ></mb-chip>`
                                    : html`<p class="text-[16px] font-medium md:text-[18px]">${this.kicker.text}</p>`,
                            )}
                            ${when(
                                this.headline,
                                html`
                                    <h1
                                        class="flex-headline1-medium"
                                    >
                                        ${this.headline}
                                    </h1>
                                `,
                            )}
                            ${when(
                                this.text,
                                html`<div class="hidden text-[16px] md:block md:text-[20px]">
                                    ${unsafeHTML(this.text)}
                                </div>`,
                            )}
                        </div>
                        ${when(
                            this.buttons?.length > 0,
                            html`
                                <div class="flex flex-col gap-16 md:flex-row">
                                    ${this.buttons.map(
                                        (button) => html`
                                            <mb-button
                                                variant="${button.variant}"
                                                href="${button.url}"
                                                class="w-full text-center md:w-auto"
                                                >${button.text}</mb-button
                                            >
                                        `,
                                    )}
                                </div>
                            `,
                        )}
                    </div>
                </div>
            </div>
        `;
    }

    fullWidthTemplate() {
        return html`
            <div class="hero-video-container">
                <div class="container content-area">
                    ${when(this.showTitle === true, html` <h1>${this.headline}</h1> `)}
                    <slot></slot>
                </div>
                ${this.videoTemplate()}
            </div>
        `;
    }

    template() {
        return this.variant === 'contained' ? this.containedTemplate() : this.fullWidthTemplate();
    }
}

defineElement('mb-hero-video', HeroVideo);
